import { EntriesList } from "@dash/history";
import { Page, PageHeader, SectionHeader } from "@dash/page";
import { SearchWithPagination } from "@dash/search";
import { QuerySkeleton } from "@dash/skeleton";
import {
  docAppLabel,
  docHref,
  docTypeIcon,
  docTypeLabel,
  HistoryBrowserEntry,
  RecentlyViewedDoc,
  useRecentlyViewedDocs,
} from "@megarax/features";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";

import { groupHistoryByDay } from "./groupHistoryByDay";

const historyLimit = 200;
const pageSize = 25;

const matchesSearch = (item: RecentlyViewedDoc, searchText: string): boolean => {
  const query = searchText.toLowerCase();
  const href = docHref(item.doc.docType, item.doc.docId) ?? "";

  return (
    (item.doc.name ?? "").toLowerCase().includes(query) ||
    href.toLowerCase().includes(query) ||
    docTypeLabel(item.doc.docType).toLowerCase().includes(query) ||
    docAppLabel(item.doc.docType).toLowerCase().includes(query)
  );
};

const formatTime = (date: Date): string =>
  date.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });

export const HistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [searchText]);

  const { data, error, refetch } = useRecentlyViewedDocs(historyLimit);

  const filtered = data?.filter((item) => matchesSearch(item, searchText)) ?? [];
  const pageItems = filtered.slice(page * pageSize, page * pageSize + pageSize);

  return (
    <Page css={{ display: "flex", flexDirection: "column", gap: "1rem", maxWidth: "100%", paddingBottom: "2rem" }}>
      <PageHeader>Historia</PageHeader>

      <SearchWithPagination
        searchValue={searchText}
        onSearchChange={setSearchText}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        itemCount={data === undefined ? undefined : filtered.length}
      />

      <QuerySkeleton query={{ data, error }} onRetry={refetch}>
        {(items) => {
          if (filtered.length === 0)
            return (
              <span css={{ opacity: 0.6, padding: "1rem 0" }}>
                {items.length === 0 ? "Brak historii przeglądania." : "Brak wyników."}
              </span>
            );

          return (
            <div css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {groupHistoryByDay(pageItems).map((group) => (
                <div key={group.key} css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <SectionHeader isHr>{group.label}</SectionHeader>

                  <EntriesList>
                    {group.items.map(({ doc, viewedAt }) => {
                      const href = docHref(doc.docType, doc.docId);
                      const Icon = docTypeIcon(doc.docType);

                      if (!href) return null;

                      return (
                        <HistoryBrowserEntry
                          key={`${doc.docId}-${viewedAt.getTime()}`}
                          title={doc.name ?? ""}
                          href={href}
                          icon={<Icon />}
                          time={formatTime(viewedAt)}
                          appLabel={docAppLabel(doc.docType)}
                          docTypeLabel={docTypeLabel(doc.docType)}
                          onClick={() => navigate(href)}
                        />
                      );
                    })}
                  </EntriesList>
                </div>
              ))}
            </div>
          );
        }}
      </QuerySkeleton>
    </Page>
  );
};
